import InvoiceWithPackageOrderView from "issara-sdk/apis/InvoiceWithPackageOrderView_apps_BIL";
import type { Controller, Handler, Params } from "../types/MeBillingType";
import {
  DEFAULT_ORDER_SUMMARY_FILTER,
  ORDER_SUMMARY_LIMIT,
} from "../types/PackageOrderSummaryType";
import type { PackageSummaryInvoice } from "../types/PackageOrderSummaryType";

const latestRequests = new WeakMap<Controller, symbol>();

export const HandleGetOrderSummary: Handler<Params<"GET_ORDER_SUMMARY">> = async (controller, params) => {
  const divisionId = params.divisionId;
  if (!Number.isInteger(divisionId) || divisionId <= 0) return;

  const previous = controller.getState().MeBillingSequence?.orderSummary;
  const filter = params.filter ?? (
    previous?.divisionId === divisionId ? previous.filter : DEFAULT_ORDER_SUMMARY_FILTER
  );
  const activePage = Math.max(1, Math.floor(params.activePage ?? 1));
  const requestId = Symbol();
  latestRequests.set(controller, requestId);

  controller.setState((prev) => ({
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      orderSummary: { divisionId, filter, activePage, items: [], total: 0, loading: true, error: null },
    },
  }));

  const isCurrentRequest = () => {
    const currentDivision = controller.getState().selectedDivision?.id ?? controller.data.division;
    return latestRequests.get(controller) === requestId && Number(currentDivision) === divisionId;
  };

  try {
    const [response, error] = await InvoiceWithPackageOrderView.list({
      apiToken: controller.apiToken,
      params: {
        for_order_summary: true,
        is_refund: false,
        division: divisionId,
        limit: ORDER_SUMMARY_LIMIT,
        offset: (activePage - 1) * ORDER_SUMMARY_LIMIT,
        ...(filter.payment_channel && { payment_channel: filter.payment_channel }),
        ...(filter.status && { status: filter.status }),
        ...(filter.search?.trim() && { search: filter.search.trim() }),
        ...(filter.from_date && { from_date: filter.from_date }),
        ...(filter.to_date && { to_date: filter.to_date }),
      },
      extra: { division: divisionId },
    });
    if (!isCurrentRequest()) return;
    if (error || !Array.isArray(response?.items) || !Number.isFinite(response?.total) ||
        !response.items.every((item: PackageSummaryInvoice) =>
          Array.isArray(item.orders) && Array.isArray(item.payment_channels))) {
      throw new Error("Invalid order summary response");
    }

    const lastPage = Math.max(1, Math.ceil(response.total / ORDER_SUMMARY_LIMIT));
    if (activePage > lastPage) {
      await HandleGetOrderSummary(controller, { ...params, filter, activePage: lastPage });
      return;
    }

    controller.setState((prev) => ({
      MeBillingSequence: {
        ...prev.MeBillingSequence,
        orderSummary: {
          divisionId, filter, activePage, items: response.items, total: response.total,
          loading: false, error: null,
        },
      },
    }));
  } catch {
    if (!isCurrentRequest()) return;
    controller.setState((prev) => ({
      MeBillingSequence: {
        ...prev.MeBillingSequence,
        orderSummary: {
          divisionId, filter, activePage, items: [], total: 0, loading: false, error: "LOAD_FAILED",
        },
      },
    }));
  }
};
